export type TableAutoRefreshMode = 'on' | 'delayed' | 'manual';

export const CREATE_REFRESH_DEBOUNCE_MS = 10_000;
export const DEFAULT_TABLE_AUTO_REFRESH: TableAutoRefreshMode = 'delayed';
/** How long a local create keeps lists dirty for remount handling. */
export const ENTITY_CREATE_DIRTY_MS = 30_000;
/** Ignore MQTT create echoes after `notifyEntityCreated` so Manual mode does not show a banner for the user's own POST. */
export const SELF_CREATE_MQTT_SUPPRESS_MS = 10_000;

export const resolveTableAutoRefreshMode = (mode?: TableAutoRefreshMode | null): TableAutoRefreshMode => {
    if (mode === 'on' || mode === 'delayed' || mode === 'manual') return mode;
    return DEFAULT_TABLE_AUTO_REFRESH;
};

export interface CreateRefreshPolicyCallbacks {
    refresh: () => void;
    registerPending: () => void;
}

type TimerHandle = ReturnType<typeof setTimeout>;

/**
 * Applies on / delayed / manual create-refresh behaviour for a single reactive list.
 * Delayed mode refreshes immediately, then coalesces further creates until the debounce window ends.
 */
export class CreateRefreshPolicy {
    private cooldownUntil = 0;
    private pendingDuringCooldown = false;
    private timer: TimerHandle | null = null;

    constructor(
        private readonly mode: TableAutoRefreshMode,
        private readonly callbacks: CreateRefreshPolicyCallbacks,
        private readonly now: () => number = () => Date.now(),
        private readonly schedule: (fn: () => void, ms: number) => TimerHandle = (fn, ms) => setTimeout(fn, ms),
        private readonly clearSchedule: (id: TimerHandle) => void = (id) => clearTimeout(id)
    ) {}

    handleCreate(): void {
        if (this.mode === 'manual') {
            this.callbacks.registerPending();
            return;
        }

        if (this.mode === 'on') {
            this.callbacks.refresh();
            return;
        }

        const now = this.now();
        if (now >= this.cooldownUntil) {
            this.callbacks.refresh();
            this.startCooldown();
            return;
        }

        this.pendingDuringCooldown = true;
    }

    dispose(): void {
        if (this.timer != null) {
            this.clearSchedule(this.timer);
            this.timer = null;
        }
        this.pendingDuringCooldown = false;
        this.cooldownUntil = 0;
    }

    private startCooldown(): void {
        this.pendingDuringCooldown = false;
        this.cooldownUntil = this.now() + CREATE_REFRESH_DEBOUNCE_MS;
        if (this.timer != null) this.clearSchedule(this.timer);
        this.timer = this.schedule(() => {
            this.timer = null;
            if (!this.pendingDuringCooldown) {
                this.cooldownUntil = 0;
                return;
            }
            this.pendingDuringCooldown = false;
            this.callbacks.refresh();
            this.startCooldown();
        }, CREATE_REFRESH_DEBOUNCE_MS);
    }
}
